import { useTranslation } from '@rhino-project/core/i18n';
import { NavIcon } from '../icons';
import { cn } from '@heroui/react';
import {
  useGlobalComponent,
  useRoles,
  useBaseOwnerId
} from '@rhino-project/core/hooks';
import {
  getBaseOwnedModels,
  getModel,
  getModelIndexPath
} from '@rhino-project/core/utils';
import { MouseEventHandler, ReactNode, useMemo } from 'react';
import { map, uniqBy } from 'lodash-es';
import { Link, type LinkProps } from '@tanstack/react-router';
import { RhinoResourceName } from '@rhino-project/core';
import { useApplicationShell } from '../shells/ApplicationShell';

export type NavSectionProps = {
  title?: string;
  children: ReactNode;
};

export const NavSection = ({ title, children }: NavSectionProps) => {
  return (
    <li data-slot="base" role="presentation" className="relative mb-2 w-full">
      {title && (
        <span role="presentation" className="pl-1 text-xs text-foreground/80">
          {title}
        </span>
      )}
      <ul>{children}</ul>
    </li>
  );
};

export type NavItemProps = {
  title: string;
  icon: string;
  disabled?: boolean;
  /** Collapse the shell's mobile drawer when the item is clicked. */
  closeMenuOnClick?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
} & Omit<LinkProps, 'to'> & { to?: string };

export const NavItem = ({
  title,
  icon,
  to,
  disabled,
  closeMenuOnClick = true,
  onClick,
  ...props
}: NavItemProps) => {
  // No-op outside a shell or at the persistent-sidebar breakpoint, where
  // isMenuOpen has no effect.
  const { setMenuOpen } = useApplicationShell();

  return (
    <li>
      <Link
        to={to as never}
        disabled={disabled}
        onClick={(e) => {
          onClick?.(e);
          if (closeMenuOnClick) setMenuOpen(false);
        }}
        className={cn(
          'group block',
          disabled && 'pointer-events-none opacity-50'
        )}
        {...props}
      >
        <div className="flex gap-2 items-center relative py-1.5 px-3 min-h-11 h-[44px] w-full box-border subpixel-antialiased cursor-pointer rounded-lg outline-none data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2 hover:transition-colors hover:bg-accent/10 dark:hover:bg-accent/20 group-data-[status=active]:bg-accent/20 dark:group-data-[status=active]:bg-accent/40">
          {icon && <NavIcon icon={icon} />}
          <span className="flex-1 truncate text-sm font-medium text-foreground/60 group-data-[status=active]:text-foreground">
            {title}
          </span>
        </div>
      </Link>
    </li>
  );
};

export type ModelNavSectionProps = {
  title?: string;
  // A role-keyed object names the models each role sees; the union of the
  // caller's roles is what gets rendered.
  models?:
    | RhinoResourceName[]
    | Record<string, RhinoResourceName[]>
    | ((roles: string[]) => RhinoResourceName[])
    | null;
};

export const ModelNavSectionBase = ({
  title,
  models = null
}: ModelNavSectionProps) => {
  const { t } = useTranslation();
  const roles = useRoles();
  const owner = String(useBaseOwnerId());

  const fullModels = useMemo(() => {
    if (!models) {
      return getBaseOwnedModels();
    }

    let generatedModels = models;
    if (typeof models === 'function') {
      generatedModels = models(roles);
    }

    if (Array.isArray(generatedModels)) {
      return generatedModels.map((m) => getModel(m));
    } else if (typeof generatedModels === 'object') {
      const byRole = generatedModels as Record<string, RhinoResourceName[]>;

      return uniqBy(
        roles.reduce<RhinoResourceName[]>(
          (previousValue, roleName) =>
            byRole[roleName]
              ? [...previousValue, ...byRole[roleName]]
              : previousValue,
          []
        ),
        (model) => model
      ).map((m) => getModel(m));
    }

    console.warn('Invalid models passed to PrimaryNavigation', models);
    return [];
  }, [models, roles]);

  return (
    <NavSection title={title ?? t('rhino.frontend.messages.resources')}>
      {map(fullModels, (m) => (
        <NavItem
          key={m.model}
          title={t(`rhino.generated.${m.model}.other`, {
            defaultValue: m.pluralReadableName
          })}
          to={`/${owner}/${getModelIndexPath(m)}`}
          icon="bi:list"
        />
      ))}
    </NavSection>
  );
};

export const ModelNavSection = (props: ModelNavSectionProps) =>
  useGlobalComponent('ModelNavSection', ModelNavSectionBase, props);
